import { CapabilityService, PermissionService } from "@/features/audio/interfaces/browser-services.interface";
import { AudioStateStore } from "@/features/audio/services/audio-state.store";
import { PermissionState } from "@/features/audio/types/permission.types";

export class PermissionManager {
    constructor(
        private readonly permissionService: PermissionService,
        private readonly capabilityService: CapabilityService,
        private readonly stateStore: AudioStateStore
    ) {
        this.stateStore.patch({ capabilities: this.capabilityService.detect() });
    }

    async refreshPermissions(): Promise<PermissionState> {
        const [notifications, microphone] = await Promise.all([
            this.permissionService.queryPermission("notifications"),
            this.permissionService.queryPermission("microphone"),
        ]);

        const permissions: PermissionState = {
            notifications,
            microphone,
            speaker: microphone,
        };

        this.stateStore.patch({ permissions });
        return permissions;
    }

    async requestAllPermissions(): Promise<void> {
        await this.permissionService.requestMicrophonePermission();
        await this.permissionService.requestNotificationPermission();
        await this.permissionService.requestSpeakerPermission();
        await this.refreshPermissions();
    }

    validatePermission(name: keyof PermissionState): boolean {
        return this.stateStore.getState().permissions[name] === "granted";
    }

    startPermissionWatchers(): () => void {
        const unsubscribeNotifications = this.permissionService.subscribeToPermissionChanges(
            "notifications",
            (state) => {
                this.stateStore.patch({
                    permissions: {
                        ...this.stateStore.getState().permissions,
                        notifications: state,
                    },
                });
            }
        );

        const unsubscribeMicrophone = this.permissionService.subscribeToPermissionChanges("microphone", (state) => {
            this.stateStore.patch({
                permissions: {
                    ...this.stateStore.getState().permissions,
                    microphone: state,
                    speaker: state,
                },
            });
        });

        return () => {
            unsubscribeNotifications();
            unsubscribeMicrophone();
        };
    }
}
